/**
 * deferred-css.js — applies the screen stylesheets that first paint does not need.
 *
 * Owns: flipping every `<link data-defer-css>` from media="print" to media="all".
 * Does not own: which stylesheets are marked — that decision lives in index.html,
 *       next to the links, with the evidence for it.
 *
 * ── Why ───────────────────────────────────────────────────────────────────────
 * index.html links 21 stylesheets and every one of them blocked first paint. On
 * the deployed site that was the largest cost left once the third-party
 * JavaScript came off the boot path and the fonts were self-hosted: not bytes —
 * gzipped, the deferred set is about 39 KB — but 15 requests the browser must
 * finish before it may draw anything at all.
 *
 * `media="print"` is the lever. A stylesheet whose media query does not match is
 * still downloaded, in parallel, at low priority, and it does NOT block
 * rendering. Setting `.media = 'all'` later applies it, and the browser has
 * usually had it in hand for some time by then.
 *
 * ── Why there is no flash ─────────────────────────────────────────────────────
 * Not because the flip is fast — because none of the deferred sheets paints
 * anything that exists at boot. index.html's `<section>` shells are empty and
 * display:none until a student navigates, and this file runs at the end of
 * parsing (module scripts are deferred), which is before app.js has bound a
 * single navigation handler. So there is no moment at which a deferred screen
 * is on screen without its stylesheet.
 *
 * That claim is checked, not asserted: `tests/deferred-css.test.js` fails if a
 * deferred stylesheet gains an unscoped selector matching boot markup — the
 * navbar, the mobile tab bar, the auth screen, the chrome overlay family, or the
 * dashboard, including the markup those render from JavaScript. Six stylesheets
 * are eager for exactly that reason, and the test names which selector pins each
 * one.
 *
 * ── The failure mode, stated plainly ──────────────────────────────────────────
 * If this file does not run, 15 stylesheets stay inert and every screen but the
 * dashboard renders unstyled. It is same-origin and precached, and if it cannot
 * load then neither can the twenty modules that make those screens work — but it
 * is a single point of failure that a plain `<link>` was not, and that is the
 * price of the change.
 */

/** @returns {number} how many links were flipped — the test asserts it is not zero. */
export function applyDeferredCss(doc = document) {
    const links = doc.querySelectorAll('link[data-defer-css]');
    links.forEach((link) => { link.media = 'all'; });
    return links.length;
}

// Runs on evaluation. Module scripts are deferred, so the document is parsed and
// every link exists; there is no readyState branch to get wrong (SKILL 1).
if (typeof document !== 'undefined' && document.querySelectorAll) {
    applyDeferredCss();
}

export default applyDeferredCss;
